import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "custom-fields-settings-modal__description typographies-paragraph-medium-regular" }
const _hoisted_2 = {
  key: 0,
  class: "custom-fields-settings-modal__loading"
}
const _hoisted_3 = {
  key: 2,
  class: "custom-fields-settings-modal__list"
}

import { Translator } from 'js/shims-vue';
import { computed, ComputedRef, inject, onMounted, Ref, ref, toRef } from 'vue';
import { useTranslationLinkParser } from 'composables/useTranslationLinkParser';
import { useDocumentFormStore } from 'js/vue/stores/document/documentFormStore';
import {
    getFilteredCustomFields,
    getUngroupedCustomFields,
} from 'Helpers/customFields/customFieldsHelpers';
import CustomFieldList from 'vcomponents/common/customFields/CustomFieldList.vue';
import TranslationText from 'vcomponents/common/TranslationText.vue';
import {
    CustomFieldType,
    type CustomField,
    type CustomFieldGroup,
} from 'js/types/customFields/customFields.type';
import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { ERROR_CATEGORIES } from 'Config/authenticated/dictionary/docForm/docFormEnums';

// Injected

export default /*@__PURE__*/_defineComponent({
  __name: 'CustomFieldsSettingsModal',
  emits: ['close'],
  setup(__props, { emit: __emit }) {

const $t = inject('$t') as Translator;

// Use
const { parseTranslation } = useTranslationLinkParser();

// Store
const documentFormStore = useDocumentFormStore();
const { customFields, validationErrors } = storeToRefs(documentFormStore);
const temporary = toRef(documentFormStore, 'temporary') as Ref<Record<string, unknown>>;

// Emits
const emit = __emit;

// Refs
const showModal = ref(false);
const isSaving = ref(false);
const isLoading = ref(false);
const openGroupIds = ref<Set<number>>(new Set());
const accordionKeyMap = ref<Map<number, number>>(new Map());

// Consts
const cancelButtonProps = {
    label: $t.__('common.actions.cancel'),
    action: () => {
        customFields.value.values = JSON.parse(JSON.stringify(temporary.value.customFields));
        emit('close');
    },
};
const coloredIconProps = {
    icon: 'custom-fields',
    color: CompassEnums.ColoredIconColorType.BRAND,
};
const description = parseTranslation($t.__('document.CustomFieldsSettingsModal.description'));

// Computed
const ungroupedCustomFields: ComputedRef<CustomFieldGroup | null> = computed(() => {
    return getUngroupedCustomFields(customFields.value.values);
});
const filteredGroupedCustomFields: ComputedRef<CustomFieldGroup[]> = computed(() => {
    return getFilteredCustomFields(customFields.value.values);
});

const getAccordionKey = (groupId: number, prefix?: string): string => {
    const key = prefix ?? String(groupId);
    const version = accordionKeyMap.value.get(groupId);
    return version !== undefined ? `${key}-${version}` : key;
};

const isAccordionOpen = (groupId: number, openByDefault: boolean): boolean => {
    return openGroupIds.value.has(groupId) || openByDefault;
};

// Methods
const handleAccordionToggle = (groupId: number, isOpen: boolean): void => {
    if (isOpen) {
        openGroupIds.value.add(groupId);
    } else {
        openGroupIds.value.delete(groupId);
    }
};

const handleSave = async (): Promise<void> => {
    isSaving.value = true;
    if (!customFields.value.values.length) {
        emit('close');
        return;
    }
    if (await documentFormStore.validateCustomFields()) {
        // so that the cancel button can reset to the new values after "saving"
        temporary.value.customFields = JSON.parse(JSON.stringify(customFields.value.values));
        validationErrors.value = validationErrors.value.filter(
            (error: { type: string }) => error.type !== ERROR_CATEGORIES.CUSTOMFIELDS,
        );
        emit('close');
    } else {
        const errorGroupIds = (customFields.value.values as CustomField[])
            .filter((cf) => cf.error)
            .map((cf) => cf.customfield_group.id);
        const closedErrorGroupIds = errorGroupIds.filter((id) => !openGroupIds.value.has(id));
        for (const id of closedErrorGroupIds) {
            accordionKeyMap.value.set(id, (accordionKeyMap.value.get(id) ?? 0) + 1);
            openGroupIds.value.add(id);
        }
    }
    isSaving.value = false;
};

const primaryAction: ComputedRef<{ label: string; loading: boolean; action: () => Promise<void> }> =
    computed(() => ({
        label: $t.__('common.actions.save'),
        loading: isSaving.value,
        action: handleSave,
    }));

const handleUpdateCustomField = (id: number, value: unknown): void => {
    const values = customFields.value.values as CustomField[];
    if (!values?.length) {
        return;
    }
    const customField = values.find((field) => field.id === id);
    if (!customField) {
        return;
    }
    if (!isEqual(customField.value, value)) {
        customField.error = null;
    }
    customField.value = value as CustomField['value'];
    if (
        [
            CustomFieldType.COMPANY_INDIVIDUAL,
            CustomFieldType.CONTACT,
            CustomFieldType.STAFF,
            CustomFieldType.ITEM,
        ].includes(customField.type)
    ) {
        const items = value as Array<{ id: number; label: string }>;
        (customField as { _embed: unknown })._embed = {
            value_details: items.map((item) => ({ id: item.id, name: item.label })),
        };
        customField.value = items.map(({ id }) => id);
    }
};

// Lifecycle
onMounted((): void => {
    showModal.value = true;
    // store a temporary copy of custom fields values to be able to reset them when clicking on cancel after saving
    temporary.value.customFields = JSON.parse(JSON.stringify(customFields.value.values));
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassEmptyState = _resolveComponent("CompassEmptyState")!
  const _component_CompassAccordion = _resolveComponent("CompassAccordion")!
  const _component_CompassModal = _resolveComponent("CompassModal")!

  return (_openBlock(), _createBlock(_component_CompassModal, {
    visible: showModal.value,
    "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
    title: _unref($t).__('document.CustomFieldsSettingsModal.title'),
    size: _ctx.CompassEnums.ModalSize.SMALL,
    class: "custom-fields-settings-modal",
    coloredIconProps: coloredIconProps,
    primaryAction: primaryAction.value,
    cancelButtonProps: cancelButtonProps,
    onClosed: _cache[2] || (_cache[2] = ($event: any) => (emit('close')))
  }, {
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, [
        _createVNode(TranslationText, { parts: _unref(description) }, null, 8 /* PROPS */, ["parts"])
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_component_CompassSkeleton),
            _createVNode(_component_CompassSkeleton, { size: "x-large" }),
            _createVNode(_component_CompassSkeleton),
            _createVNode(_component_CompassSkeleton, { size: "x-large" }),
            _createVNode(_component_CompassSkeleton),
            _createVNode(_component_CompassSkeleton, { size: "x-large" })
          ]))
        : (!_unref(customFields).values.length)
          ? (_openBlock(), _createBlock(_component_CompassEmptyState, {
              key: 1,
              illustrationPath: "/ressources/logged/img/illustrations/empty-custom-fields.svg",
              title: _unref($t).__('document.CustomFieldsSettingsModal.empty_state.title'),
              caption: _unref($t).__('document.CustomFieldsSettingsModal.empty_state.description')
            }, null, 8 /* PROPS */, ["title", "caption"]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGroupedCustomFields.value, (group) => {
                return (_openBlock(), _createBlock(_component_CompassAccordion, {
                  key: getAccordionKey(group.id),
                  title: group.name,
                  open: isAccordionOpen(group.id, group.openByDefault),
                  variant: _ctx.CompassEnums.AccordionVariantType.H3,
                  onToggle: (isOpen) => handleAccordionToggle(group.id, isOpen)
                }, {
                  default: _withCtx(() => [
                    _createVNode(CustomFieldList, {
                      customFields: group.customFields,
                      shouldDisplayInfoOnPdf: "",
                      "onUpdate:customField": handleUpdateCustomField
                    }, null, 8 /* PROPS */, ["customFields"])
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["title", "open", "variant", "onToggle"]))
              }), 128 /* KEYED_FRAGMENT */)),
              (ungroupedCustomFields.value?.customFields.length)
                ? (_openBlock(), _createBlock(_component_CompassAccordion, {
                    key: getAccordionKey(0, 'ungrouped'),
                    title: _unref($t).__('common.labels.customFields.title_accordion_cf_ungrouped'),
                    variant: _ctx.CompassEnums.AccordionVariantType.H3,
                    open: isAccordionOpen(0, ungroupedCustomFields.value.openByDefault),
                    onToggle: _cache[0] || (_cache[0] = (isOpen) => handleAccordionToggle(0, isOpen))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(CustomFieldList, {
                        customFields: ungroupedCustomFields.value.customFields,
                        shouldDisplayInfoOnPdf: "",
                        "onUpdate:customField": handleUpdateCustomField
                      }, null, 8 /* PROPS */, ["customFields"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["title", "variant", "open"]))
                : _createCommentVNode("v-if", true)
            ]))
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["visible", "title", "size", "primaryAction"]))
}
}

})